import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTr } from "@/i18n/translator";
import { Lock, Inbox, CheckCircle2, Clock, X, AlertCircle } from "lucide-react";

const STATUSES = [
  { id: "open", label: "ABIERTO", color: "bg-[#E53935] text-white" },
  { id: "in_progress", label: "EN PROGRESO", color: "bg-[#FFD600]" },
  { id: "resolved", label: "RESUELTO", color: "bg-[#0A0A0A] text-[#FFD600]" },
  { id: "closed", label: "CERRADO", color: "bg-[#E5E5E5]" },
];

const CATEGORY_LABEL = {
  bug: "Bug",
  billing: "Facturación",
  feature: "Mejora",
  general: "General",
};

export default function AdminSupport() {
  const tr = useTr();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("");
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const params = filter ? `?status=${filter}` : "";
      const { data } = await api.get(`/support/tickets${params}`);
      setTickets(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Error");
    }
  };

  useEffect(() => { if (isAdmin) load(); else setErr("Requiere permisos de administrador"); }, [isAdmin, filter]);

  const open = (t) => {
    setSel(t);
    setNote(t.admin_note || "");
  };

  const update = async (status) => {
    if (!sel) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/support/tickets/${sel.id}`, { status, admin_note: note });
      setSel(data);
      toast.success(tr("Ticket actualizado"));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  if (err) {
    return (
      <div className="bg-white border border-[#0A0A0A] p-10 text-center" data-testid="admin-support-denied">
        <Lock className="w-8 h-8 mx-auto text-[#E53935]" />
        <div className="font-mono-mark text-[10px] text-[#5C5C5C] mt-3">ACCESO DENEGADO</div>
        <div className="font-display text-xl mt-2">{err}</div>
      </div>
    );
  }

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s.id]: tickets.filter((t) => t.status === s.id).length }), {});

  return (
    <div className="space-y-6" data-testid="admin-support-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("ADMIN · BANDEJA DE SOPORTE")}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 flex items-center gap-3">
          <Inbox className="w-10 h-10 text-[#E53935]" />
          {tr("Soporte")}
        </h1>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setFilter("")}
          className={`font-mono-mark text-[10px] px-3 py-2 border ${filter === "" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#0A0A0A]"}`}
          data-testid="support-filter-all"
        >
          TODOS ({tickets.length})
        </button>
        {STATUSES.map((s) => (
          <button
            key={s.id}
            onClick={() => setFilter(s.id)}
            className={`font-mono-mark text-[10px] px-3 py-2 border ${filter === s.id ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#0A0A0A]"}`}
            data-testid={`support-filter-${s.id}`}
          >
            {s.label} ({counts[s.id] || 0})
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#0A0A0A] overflow-x-auto">
        {tickets.length === 0 ? (
          <div className="p-10 text-center font-mono-data text-sm text-[#5C5C5C]">
            {tr("Sin tickets todavía.")}
          </div>
        ) : (
          <table className="w-full text-sm font-mono-data">
            <thead className="bg-[#0A0A0A] text-white">
              <tr>
                <th className="text-left px-3 py-3 font-mono-mark text-[10px]">FECHA</th>
                <th className="text-left px-3 py-3 font-mono-mark text-[10px]">EMAIL</th>
                <th className="text-left px-3 py-3 font-mono-mark text-[10px]">ASUNTO</th>
                <th className="text-left px-3 py-3 font-mono-mark text-[10px]">CATEGORÍA</th>
                <th className="text-left px-3 py-3 font-mono-mark text-[10px]">ESTADO</th>
                <th className="text-right px-3 py-3 font-mono-mark text-[10px]">ACCIÓN</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => {
                const st = STATUSES.find((x) => x.id === t.status) || STATUSES[0];
                return (
                  <tr key={t.id} className="border-t border-[#0A0A0A]" data-testid={`support-ticket-row-${t.id}`}>
                    <td className="px-3 py-3 text-xs text-[#5C5C5C]">{new Date(t.created_at).toLocaleDateString("es-MX")}</td>
                    <td className="px-3 py-3 truncate max-w-[220px]">{t.email}</td>
                    <td className="px-3 py-3 truncate max-w-[280px]">{t.subject}</td>
                    <td className="px-3 py-3">{CATEGORY_LABEL[t.category] || t.category}</td>
                    <td className="px-3 py-3">
                      <span className={`font-mono-mark text-[9px] px-2 py-0.5 ${st.color}`}>{st.label}</span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={() => open(t)} className="font-mono-mark text-[10px] bg-[#0A0A0A] text-white px-3 py-1.5 border border-[#0A0A0A] hover-brutal" data-testid={`support-open-${t.id}`}>
                        ABRIR
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {sel && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSel(null)}>
          <div className="bg-white border border-[#0A0A0A] brutal-shadow max-w-2xl w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="support-ticket-modal">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="font-mono-mark text-[10px] text-[#5C5C5C]">TICKET · {sel.id.slice(0, 8)}</div>
                <h2 className="font-display text-2xl mt-1 break-words">{sel.subject}</h2>
                <div className="font-mono-data text-xs text-[#5C5C5C] mt-2">
                  {sel.name} · <a href={`mailto:${sel.email}`} className="underline">{sel.email}</a> · {new Date(sel.created_at).toLocaleString("es-MX")}
                </div>
              </div>
              <button onClick={() => setSel(null)} className="text-[#5C5C5C] hover:text-[#E53935]"><X className="w-5 h-5" /></button>
            </div>

            <div className="bg-[#F4F4F0] border border-[#0A0A0A] p-4">
              <div className="font-mono-mark text-[9px] text-[#5C5C5C] mb-2">MENSAJE DEL USUARIO</div>
              <div className="whitespace-pre-wrap font-mono-data text-sm">{sel.message}</div>
            </div>

            <div>
              <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-2">NOTA INTERNA (admin)</div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                className="w-full bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm"
                placeholder={tr("Seguimiento interno…")}
                data-testid="support-admin-note"
              />
            </div>

            <div className="flex flex-wrap gap-2 border-t border-[#0A0A0A] pt-3">
              <button onClick={() => update("in_progress")} disabled={saving} className="font-mono-mark text-[10px] bg-[#FFD600] px-3 py-2 border border-[#0A0A0A] flex items-center gap-2" data-testid="support-status-in-progress">
                <Clock className="w-3 h-3" /> EN PROGRESO
              </button>
              <button onClick={() => update("resolved")} disabled={saving} className="font-mono-mark text-[10px] bg-[#0A0A0A] text-[#FFD600] px-3 py-2 border border-[#0A0A0A] flex items-center gap-2" data-testid="support-status-resolved">
                <CheckCircle2 className="w-3 h-3" /> RESUELTO
              </button>
              <button onClick={() => update("closed")} disabled={saving} className="font-mono-mark text-[10px] bg-[#E5E5E5] px-3 py-2 border border-[#0A0A0A] flex items-center gap-2" data-testid="support-status-closed">
                CERRAR
              </button>
              <button onClick={() => update("open")} disabled={saving} className="font-mono-mark text-[10px] bg-white px-3 py-2 border border-[#0A0A0A] flex items-center gap-2 ml-auto" data-testid="support-status-reopen">
                <AlertCircle className="w-3 h-3" /> REABRIR
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
